'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["editor", "onInsert", "onEdit", "mode", "onModeChange", "onTokenRemove", "dataTokenConfig"];
import { useState, useEffect, useCallback, useRef } from 'react';
import DataTokenPopoverBodyInsert from 'rich-text-lib/components/dataToken/DataTokenPopoverBodyInsert';
import DataTokenPopoverBodyBranch from 'rich-text-lib/components/dataToken/DataTokenPopoverBodyBranch';
import { PopoverType, PopoverBodyMode } from 'rich-text-lib/constants/popover';
import { getDataTokenValues } from '../utils/token';
import useInlineTarget from '../utils/hooks/useInlineTarget';
import TinyMcePopover from './TinyMcePopover';
import withPopoverModeState from './withPopoverModeState';
import { INLINE_TARGET_ATTR } from '../constants/inlineTarget';
import { HS_DATA_TOKEN } from '../constants/plugins';
import { jsx as _jsx } from "react/jsx-runtime";
function DataTokenPopoverManager(props) {
  const {
      editor,
      onInsert,
      onEdit,
      mode,
      onModeChange,
      onTokenRemove,
      dataTokenConfig
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    renderDataTokenPopover = null
  } = dataTokenConfig;
  const [popoverType, setPopoverType] = useState(PopoverType.INSERT);
  const {
    inlineTarget,
    setInlineTarget,
    initInlineTarget
  } = useInlineTarget(editor, null);
  const [property, setProperty] = useState();
  const lastTarget = useRef(inlineTarget);
  const lastPopoverType = useRef(popoverType);

  // Store values in immutable refs for useEffect optimization
  useEffect(() => {
    lastTarget.current = inlineTarget;
    lastPopoverType.current = popoverType;
  });
  const handleRemove = useCallback(({
    restoreSelection
  } = {}) => {
    if (lastTarget.current) {
      setInlineTarget(null, {
        restoreSelection
      });
      setProperty();
    }
  }, [setInlineTarget]);
  const handleInsert = (...args) => {
    handleRemove();
    onInsert(...args);
  };
  const handleEdit = (...args) => {
    handleRemove();
    onEdit(...args);
  };
  const handleTokenRemove = () => {
    handleRemove();
    onTokenRemove(inlineTarget);
  };
  useEffect(() => {
    const handleOpenPopover = () => {
      if (lastTarget.current) {
        handleRemove({
          restoreSelection: true
        });
      } else {
        setPopoverType(PopoverType.INSERT);
        initInlineTarget();
      }
    };
    editor.on('OpenPersonalizePopover', handleOpenPopover);
    return () => editor.off('OpenPersonalizePopover', handleOpenPopover);
  }, [editor, initInlineTarget, handleRemove]);
  useEffect(() => {
    const handleHsNodeChange = ({
      element
    }) => {
      if (!element) {
        handleRemove();
        return;
      }
      if (element.hasAttribute(INLINE_TARGET_ATTR)) {
        // This ignores the insert popover scenario (and for non-ptokens)
        return;
      }
      onModeChange(PopoverBodyMode.PREVIEW);
      if (element !== lastTarget.current) {
        setInlineTarget(null);
        setPopoverType(PopoverType.EDIT);
        setProperty(getDataTokenValues(dataTokenConfig, element));
        setInlineTarget(element);
      }
    };
    editor.on(`hsNodeChange:${HS_DATA_TOKEN}`, handleHsNodeChange);
    return () => editor.off(`hsNodeChange:${HS_DATA_TOKEN}`, handleHsNodeChange);
  }, [editor, setInlineTarget, handleRemove, onModeChange, dataTokenConfig]);
  const isInsert = popoverType === PopoverType.INSERT;
  let PopoverContent;
  let popoverContentProps = {};
  if (isInsert) {
    PopoverContent = DataTokenPopoverBodyInsert;
    popoverContentProps = {
      onInsert: handleInsert,
      onClose: handleRemove
    };
  } else {
    if (renderDataTokenPopover) {
      PopoverContent = renderDataTokenPopover();
      popoverContentProps = {
        onTokenRemove: handleTokenRemove
      };
    } else {
      PopoverContent = DataTokenPopoverBodyBranch;
      popoverContentProps = {
        mode,
        onModeChange,
        onEdit: handleEdit,
        onTokenRemove: handleTokenRemove
      };
    }
  }
  const onEditCategory = useCallback(category => void setProperty(currentProperty => Object.assign({}, currentProperty || {}, {
    category
  })), [setProperty]);
  const onEditField = useCallback(field => void setProperty(currentProperty => Object.assign({}, currentProperty || {}, {
    field
  })), [setProperty]);
  return /*#__PURE__*/_jsx(TinyMcePopover, {
    editor: editor,
    "data-test-id": "data-token-browser-popover",
    open: !!inlineTarget,
    target: inlineTarget,
    placement: "bottom",
    onRemove: handleRemove,
    animateOnToggle: false,
    renderContent: () => /*#__PURE__*/_jsx(PopoverContent, Object.assign({}, rest, popoverContentProps, {
      dataTokenConfig: dataTokenConfig,
      apiClient: editor.settings.apiClient,
      cacheKey: editor.id,
      property: property,
      onEditCategory: onEditCategory,
      onEditField: onEditField,
      tooltipClassName: "mce-token-tooltip"
    }))
  });
}
export default withPopoverModeState(DataTokenPopoverManager);